/**
 * Partner-id → logo_url map, merged from every place the API hands out a logo.
 *
 * As of Round-2 (live 2026-07-15) the account row itself carries `logo_url`
 * (`GET /auth/account/partners`), so that is now the PRIMARY source for connected
 * venues (Desk grid, balances). We still harvest from orders + opportunities as a
 * fallback for any venue not present on the account summary. react-query dedups the
 * underlying fetches, so calling this on multiple pages is cheap.
 */
import { useMemo } from "react";
import type { Opportunity, Order } from "@greenbook/client";
import { useAccountPartners, useOpenPlayOpportunities, useOrders } from "./queries.js";

export function usePartnerLogos(): Map<string, string> {
  const partnersQ = useAccountPartners();
  // The open plays' opportunities (also read by the nav badge and Portfolio): one shared request.
  const oppsQ = useOpenPlayOpportunities();
  const ordersQ = useOrders(50);

  return useMemo(() => {
    const map = new Map<string, string>();
    const add = (id?: string | null, url?: string | null) => {
      if (id && url && !map.has(id)) map.set(id, url);
    };

    // Primary: the account row's own logo_url (first-wins → highest priority).
    for (const ap of partnersQ.data?.account_partners ?? []) {
      add(ap.partner_id, ap.logo_url);
    }

    const opps: Opportunity[] = oppsQ.data ?? [];
    for (const o of opps) {
      for (const p of o.partners ?? []) add(p.partner_id, p.logo_url);
      for (const leg of o.legs ?? []) add(leg.partner?.partner_id ?? leg.partner_id, leg.partner?.logo_url);
    }

    const orders: Order[] = (ordersQ.data?.pages ?? []).flatMap((pg) => pg.data);
    for (const ord of orders) {
      add(ord.partner?.partner_id ?? ord.partner_id, ord.partner?.logo_url);
    }
    return map;
  }, [partnersQ.data, oppsQ.data, ordersQ.data]);
}
